import { Controller } from 'stimulus'
import $ from 'jquery'
import { submitForm } from '~/common/helpers/form'
import './results_component.scss'

export default class extends Controller {
  connect() {
    this.onPopState = () => {
      if (this.isTurboNative) return
      // The persistent #search_form must match the restored URL too — otherwise a
      // filter click right after Back/Forward would serialize the newer (stale)
      // content_type/facet_* the form still holds, producing results that don't
      // match what's now on screen.
      this.syncFormFromUrl(window.location.href)
      this.load(window.location.href, { pushState: false })
    }
    window.addEventListener('popstate', this.onPopState)
  }

  disconnect() {
    window.removeEventListener('popstate', this.onPopState)
  }

  selectContentType(event) {
    const form = document.getElementById('search_form')
    this.setHiddenField(form, 'content_type', event.params.value)
    this.clearHiddenField(form, 'page')
    this.clearFacetFields(form)
    this.submit(form)
  }

  selectFacetValue(event) {
    const form = document.getElementById('search_form')
    this.setHiddenField(form, event.params.name, event.params.value)
    this.clearHiddenField(form, 'page')
    this.submit(form)
  }

  // Prev/Next links. Left untouched (native navigation) inside the Turbo Native
  // app so its existing replace_action_if_turbo_native nav-stack transition
  // keeps working (see ENG-11940/PR #22260) — only the plain web case gets fetched.
  goToPage(event) {
    if (this.isTurboNative) return

    event.preventDefault()
    this.load(event.currentTarget.href)
  }

  submit(form) {
    if (this.isTurboNative) {
      submitForm(form)
      return
    }

    this.load(`${form.action}?${$(form).serialize()}`)
  }

  // Aborts any in-flight request first — otherwise a slower response from an earlier
  // click could land after a faster one and silently overwrite the user's latest
  // selection with stale results (or replace an already-detached element, a no-op).
  load(url, { pushState = true } = {}) {
    const previousQuery = this.lastQuery
    this.element.setAttribute('aria-busy', 'true')

    const query = $.ajax({
      type: 'GET',
      url,
      dataType: 'json',
      global: false,
      cache: false,
    })
    this.lastQuery = query
    // Abort only after this.lastQuery already points at the new query, so the
    // aborted request's own .fail() guard (below) sees itself as superseded
    // and exits quietly instead of clearing aria-busy for the request replacing it.
    previousQuery?.abort?.()

    query
      .done((data) => {
        if (this.lastQuery !== query) return
        if (pushState) window.history.pushState(null, '', url)
        const $replacement = $(data.lazy_content)
        $(this.element).replaceWith($replacement)
        $replacement.changed()
      })
      .fail((_, status, error) => {
        if (this.lastQuery !== query) return
        this.element.removeAttribute('aria-busy')
        console.error('Error loading search results:', status, error)
      })
  }

  get isTurboNative() {
    return document.body.dataset.turboNativeApp === 'true'
  }

  // Only syncs the fields this controller itself ever mutates (content_type, page,
  // facet_*) — q and the other hidden fields (course_id, forum_id, ...) are set once
  // at initial page render and never change across an AJAX filter/page navigation.
  syncFormFromUrl(url) {
    const form = document.getElementById('search_form')
    if (!form) return

    const params = new URL(url, window.location.href).searchParams
    this.clearFacetFields(form)

    for (const name of ['content_type', 'page']) {
      const value = params.get(name)
      if (value) this.setHiddenField(form, name, value)
      else this.clearHiddenField(form, name)
    }

    for (const [name, value] of params) {
      if (name.startsWith('facet_')) this.setHiddenField(form, name, value)
    }
  }

  setHiddenField(form, name, value) {
    let field = form.querySelector(`input[name=${name}]`)
    if (field) {
      field.value = value
    } else {
      let field = document.createElement('input')
      field.setAttribute('type', 'hidden')
      field.setAttribute('name', name)
      field.setAttribute('value', value)
      form.appendChild(field)
    }
  }

  clearHiddenField(form, name) {
    let field = form.querySelector(`input[name=${name}]`)
    if (field) field.remove()
  }

  // Facets are configured per content type, so a facet selected under one type is
  // meaningless (and would over-filter) once the user switches to another.
  clearFacetFields(form) {
    form
      .querySelectorAll('input[name^="facet_"]')
      .forEach((field) => field.remove())
  }
}
